import {
  disponibilidadeFrotaPatioStorageKey,
  posicoesFrotaPatioStorageKey
} from '../utils/constants';
import {
  getPlotagemLabel,
  getRemainingPositionRefreshMs,
  getRemainingRefreshSeconds
} from '../utils/formatters';
import {
  getCategoriaVeiculo,
  isReduxTipoModeloMapped,
  mapResumoFrotasFromMapped
} from '../utils/mappers';
import {
  serializePositionMap,
  stripTransientPositionData
} from '../utils/positions';

const posicoesCacheVersion = 2;

const normalizeCachedFrota = item => ({
  ...item,
  plotagem: getPlotagemLabel(item?.idTipoEmblema ?? item?.plotagem)
});

export const clearPosicoesCache = () => {
  if (typeof window === 'undefined') return;

  window.localStorage.removeItem(posicoesFrotaPatioStorageKey);
};

export const readDisponibilidadeCache = () => {
  if (typeof window === 'undefined') return null;

  try {
    const cachedValue = window.localStorage.getItem(disponibilidadeFrotaPatioStorageKey);

    if (!cachedValue) return null;

    const parsedValue = JSON.parse(cachedValue);
    const { dataAtualizacao, ...cachedData } = parsedValue || {};

    if (!dataAtualizacao) return null;

    const updatedAt = new Date(dataAtualizacao);

    if (Number.isNaN(updatedAt.getTime())) {
      window.localStorage.removeItem(disponibilidadeFrotaPatioStorageKey);
      return null;
    }

    const cachedFrotasTracao = (cachedData?.frotasTracao || [])
      .filter(item => !isReduxTipoModeloMapped(item))
      .map(item => ({
        ...normalizeCachedFrota(item),
        categoria: item?.categoria || getCategoriaVeiculo(item),
        numeroFrota: item?.numeroFrota || item?.frota || '',
        posicao: item?.posicao || null
      }));

    const cachedFrotasReboque = (cachedData?.frotasReboque || [])
      .filter(item => !isReduxTipoModeloMapped(item))
      .map(item => ({
        ...normalizeCachedFrota(item),
        categoria: 'carreta',
        numeroFrota: item?.numeroFrota || item?.frota || '',
        posicao: item?.posicao || null
      }));

    return {
      data: {
        motoristas: cachedData?.motoristas || [],
        frotasTracao: cachedFrotasTracao,
        frotasReboque: cachedFrotasReboque,
        resumoFrotas: mapResumoFrotasFromMapped([...cachedFrotasTracao, ...cachedFrotasReboque]),
        posicoesPorChave: cachedData?.posicoesPorChave || {}
      },
      dataAtualizacao,
      updatedAt
    };
  } catch (error) {
    console.error('Erro ao ler cache da disponibilidade frota patio:', error);
    return null;
  }
};

export const getValidDisponibilidadeCache = () => {
  const cachedDisponibilidade = readDisponibilidadeCache();

  if (!cachedDisponibilidade) return null;

  if (getRemainingRefreshSeconds(cachedDisponibilidade.dataAtualizacao) <= 0) {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(disponibilidadeFrotaPatioStorageKey);
    }

    return null;
  }

  return cachedDisponibilidade;
};

export const readPosicoesCache = () => {
  if (typeof window === 'undefined') return null;

  try {
    const cachedValue = window.localStorage.getItem(posicoesFrotaPatioStorageKey);

    if (!cachedValue) return null;

    const parsedValue = JSON.parse(cachedValue);
    const dataAtualizacao = parsedValue?.dataAtualizacao || null;

    if (parsedValue?.version !== posicoesCacheVersion || !dataAtualizacao) {
      clearPosicoesCache();
      return null;
    }

    const updatedAt = new Date(dataAtualizacao);

    if (Number.isNaN(updatedAt.getTime())) {
      clearPosicoesCache();
      return null;
    }

    return {
      posicoesPorChave: serializePositionMap(parsedValue?.posicoesPorChave || {}),
      dataAtualizacao,
      updatedAt
    };
  } catch (error) {
    console.error('Erro ao ler cache de posicoes da disponibilidade frota patio:', error);
    clearPosicoesCache();
    return null;
  }
};

export const getValidPosicoesCache = () => {
  const cachedPosicoes = readPosicoesCache();

  if (!cachedPosicoes) return null;

  if (getRemainingPositionRefreshMs(cachedPosicoes.dataAtualizacao) <= 0) {
    clearPosicoesCache();
    return null;
  }

  return cachedPosicoes;
};

export const saveDisponibilidadeCache = data => {
  if (typeof window === 'undefined') return null;

  try {
    const dataAtualizacao = new Date().toISOString();
    const serializableData = stripTransientPositionData(data);

    window.localStorage.setItem(
      disponibilidadeFrotaPatioStorageKey,
      JSON.stringify({
        motoristas: serializableData.motoristas,
        frotasTracao: serializableData.frotasTracao,
        frotasReboque: serializableData.frotasReboque,
        resumoFrotas: serializableData.resumoFrotas,
        dataAtualizacao
      })
    );

    return dataAtualizacao;
  } catch (error) {
    console.error('Erro ao salvar cache da disponibilidade frota patio:', error);
    return null;
  }
};

export const savePosicoesCache = positionMap => {
  if (typeof window === 'undefined') return null;

  try {
    const dataAtualizacao = new Date().toISOString();
    const serializableMap = serializePositionMap(positionMap);

    window.localStorage.setItem(
      posicoesFrotaPatioStorageKey,
      JSON.stringify({
        posicoesPorChave: serializableMap,
        dataAtualizacao,
        version: posicoesCacheVersion
      })
    );

    return dataAtualizacao;
  } catch (error) {
    console.error('Erro ao salvar cache de posicoes da disponibilidade frota patio:', error);
    return null;
  }
};
